import { createSelector } from '@reduxjs/toolkit';
import { Filter, OperatorType } from 'vet-bones/utils/queryBuilder';

import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { projectRulesByIdSelector } from 'src/selectors/projectRules';
import { RDF_TYPE_IRI } from 'src/templates/queries/iris';

export const projectRulesForTreeSelector = (projectId: string) =>
  createSelector(projectRulesByIdSelector(projectId), (rulesById) => {
    const rulesByClass: Record<
      string,
      {
        rules: Set<string>;
        attributes: Set<string>;
        relationships: Set<string>;
      }
    > = {};

    const processFilterForRule = (rule: ModelRule) => (filter: Filter) => {
      const operatorIri = filter.operator?.iri;
      if (operatorIri === RDF_TYPE_IRI) {
        // ignore "Classify as" for now
        return;
      }

      const subjectIri = filter.subject?.iri || null;
      let sourceInfo = subjectIri ? rulesByClass[subjectIri] : null;
      if (subjectIri && !sourceInfo) {
        sourceInfo = {
          rules: new Set(),
          attributes: new Set(),
          relationships: new Set(),
        };
        rulesByClass[subjectIri] = sourceInfo;
      }
      sourceInfo?.rules.add(rule.id);

      const targetIri =
        typeof filter.value === 'object' ? filter.value?.iri : null;
      let targetInfo = targetIri ? rulesByClass[targetIri] : null;
      if (targetIri && !targetInfo) {
        targetInfo = {
          rules: new Set(),
          attributes: new Set(),
          relationships: new Set(),
        };
        rulesByClass[targetIri] = targetInfo;
      }
      targetInfo?.rules.add(rule.id);

      if (operatorIri) {
        if (filter.operator?.operatorType === OperatorType.DATATYPE) {
          sourceInfo?.attributes.add(operatorIri);
        } else if (filter.operator?.operatorType === OperatorType.OBJECT) {
          sourceInfo?.relationships.add(operatorIri);
          targetInfo?.relationships.add(operatorIri);
        }
      }
    };

    Object.values(rulesById).forEach((rule) => {
      const processFilter = processFilterForRule(rule);
      if (rule.ifClause) {
        rule.ifClause.data.query.expressions.forEach((subExpression) =>
          subExpression.expressions.forEach((expression) => {
            expression.filters.forEach((filter) => processFilter(filter));
          })
        );
      }
      if (rule.thenClause) {
        rule.thenClause.data.query.expressions.forEach((subExpression) =>
          subExpression.expressions.forEach((expression) => {
            expression.filters.forEach((filter) => processFilter(filter));
          })
        );
      }
    });

    return rulesByClass;
  });
